import { TooltipButton } from '@/ui/TooltipButton';
// PostActions.tsx — action row: Comment / Cherish / Spread-It! / Share / Bookmark
//
// Two reactions, one job each. Cherish is the ONE appreciation (it replaced Like);
// Spread-It! is the ONE distribution act (it puts the post in your followers' feeds).
//
// Behavior spec:
//   • Comment  → <Link> to /post/<id> by default; when `onComment` is provided, renders as a
//                button and calls `onComment` (no navigation). Not auth-gated.
//   • Cherish  → driven by the server's post.cheered; toggleCheer on click, auth-gated.
//   • Spread   → driven by post.spread; a toggle (spread again to take it back), auth-gated.
//                Hidden for an unlisted video, which is never pushed into anyone's feed.
//   • Share    → Popover with "Quote" (auth-gated) and "Share…" (opens SharePostDialog —
//                NOT auth-gated: a guest may share a public post; nothing about it mutates
//                the post). Each closes the popover.
//
// Cherish and Spread update at once and say so if the server refuses: a tap that
// silently does nothing reads as a broken button.
//   • Bookmark → only rendered when `showBookmark` is true; toggles via usePostBookmark, auth-gated.
//
// Props:
//   post          — required, always
//   onComment?    — when provided, Comment is a <button> calling this; else a <Link>
//   showBookmark? — render the Bookmark button (post-detail hero)
//   size?         — 'md' (default, feed) | 'lg' (hero); controls icon size

import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import type { Post } from '@/core/models/post';
import { postPath } from '@/core/lib/postUrl';
import { ApiError } from '@/core/lib/apiClient';
import { Icon } from '@/icons';
import { Popover, DisabledHint, useToast } from '@/ui';
import { usePosts, useRequireAuth, usePostBookmark } from '@/hooks';
import { useQuoteDraftStore } from '@/core/stores/quoteDraftStore';
import { useComposeSheetStore } from '@/core/stores/composeSheetStore';
import { SharePostDialog } from './SharePostDialog';
import { ActionDivider } from './ActionDivider';

// ─── Styled primitives ────────────────────────────────────────────────────────

const ActionArea = styled.div`
  min-width: 0;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const baseButtonStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px;
  min-width: 44px;
  min-height: 44px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  cursor: pointer;
  transition:
    color 0.15s,
    background 0.15s;
  text-decoration: none;
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
    background: ${({ theme }) => theme.color.glassBorder};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const ActionBtn = styled(TooltipButton)`
  ${baseButtonStyle}
`;

const ActionLink = styled(Link)`
  ${baseButtonStyle}
`;

/* Engagement toggles: active is WEIGHT, not colour.
   Until 1.25.00 Cherish glowed in accentCyan, Like in accentMagenta and
   Bookmark in accentCyan again — two purples about 6% apart in lightness, each
   wearing a drop-shadow halo. Three problems at once: a neon glow on an
   engagement icon is the "make it pop" reflex, colour was the only signal
   (which fails for anyone who cannot separate those two purples), and it spent
   the record's ink on a Like. The accent means "this is on the record"; liking
   a post is not a record. Active now reads as full-strength text at a heavier
   weight, which is legible without colour vision and needs no halo. */
const activeMark = css`
  color: ${({ theme }) => theme.color.textPrimary};
  font-weight: 600;
`;

const CherishBtn = styled(ActionBtn)<{ $active: boolean }>`
  ${({ $active }) => $active && activeMark}
`;

const SpreadBtn = styled(ActionBtn)<{ $active: boolean }>`
  ${({ $active }) => $active && activeMark}
`;

const BookmarkBtn = styled(ActionBtn)<{ $active: boolean }>`
  ${({ $active }) => $active && activeMark}
`;

// A menu item inside the Share popover panel
const SpreadItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;

  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export interface PostActionsProps {
  post: Post;
  /** When provided, the Comment control is a button calling this (instead of a Link). */
  onComment?: () => void;
  /** Render the Bookmark button (post-detail hero). */
  showBookmark?: boolean;
  /** Icon scale. 'lg' for the hero card. */
  size?: 'md' | 'lg';
  /**
   * Set for an author-deleted OR still-scheduled post: Comment/Cherish/Spread/Share
   * go dead server-side (see PostViewSet — both a deleted and an unpublished post
   * 409 every interaction), so they render via the DisabledHint contract here
   * rather than as silently-broken buttons. The reason drives the explanation
   * text — a scheduled draft is not "deleted". Bookmark stays live either way —
   * neither state blocks bookmarking.
   */
  disabledReason?: 'deleted' | 'scheduled';
}

const DISABLED_EXPLANATION: Record<'deleted' | 'scheduled', string> = {
  deleted: 'This post has been deleted. Interactions are off.',
  scheduled: 'Scheduled. Interactions turn on when it publishes.',
};

const suppressClick = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
  e.preventDefault();
  e.stopPropagation();
};

export function PostActions({
  post,
  onComment,
  showBookmark,
  size = 'md',
  disabledReason,
}: PostActionsProps) {
  const { toggleCheer, spread } = usePosts();
  const requireAuth = useRequireAuth();
  const { isBookmarked, toggle: toggleBookmark } = usePostBookmark(post);
  const toast = useToast();
  const [shareOpen, setShareOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  // The Share trigger, for focus return when the share sheet closes. Dialog's own
  // focus-restore can't help here: by the time it mounts, the popover item that had
  // focus is already gone (the popover closes in the same commit), so document's
  // activeElement is <body>. Popover clones the trigger and preserves this ref.
  const shareTriggerRef = useRef<HTMLButtonElement>(null);
  const setPendingQuote = useQuoteDraftStore((s) => s.setPendingQuote);
  const openComposeSheet = useComposeSheetStore((s) => s.openSheet);

  const disabled = disabledReason !== undefined;
  const explanation = DISABLED_EXPLANATION[disabledReason ?? 'deleted'];
  const iconSize = size === 'lg' ? 20 : 18;

  // Driven by the server, not by local state. Previously this was useState(false), so after a
  // reload a post you HAD cherished showed as un-cherished — and clicking it then lit the icon
  // on while the server, seeing an existing cheer, removed it and the count went DOWN.
  // toggleCheer patches the store, so the new value arrives back through `post`.
  const cheered = post.cheered === true;

  const hasSpread = post.spread === true;
  const canSpread = post.audience?.type !== 'unlisted';

  // An ended session (401) and an unverified email already get their own message from
  // the shell; a second toast about the same tap would only be noise.
  const alreadyExplained = (e: unknown) =>
    e instanceof ApiError && (e.status === 401 || e.code === 'email_unverified');

  function handleCherish() {
    if (!requireAuth('cherish')) return;
    // Optimistic in the service; it restores the post and rethrows on failure.
    Promise.resolve(toggleCheer(post.id)).catch((e: unknown) => {
      if (!alreadyExplained(e)) toast("Couldn't save your Cherish. Try again.");
    });
  }

  function handleSpread() {
    if (!requireAuth('spread')) return;
    const undoing = hasSpread;
    Promise.resolve(spread(post.id)).then(
      () => toast(undoing ? 'Spread taken back' : 'Spread to your followers'),
      (e: unknown) => {
        if (!alreadyExplained(e)) toast("Couldn't spread this post. Try again.");
      },
    );
  }

  function handleQuote() {
    if (!requireAuth('quote')) return;
    setShareOpen(false);
    // The shell hosts the composer on every viewport and route.
    setPendingQuote(post);
    openComposeSheet();
  }

  // Deliberately NOT behind requireAuth — see the header comment.
  function handleShareSheet() {
    setShareOpen(false);
    setShareDialogOpen(true);
  }

  function closeShareSheet() {
    setShareDialogOpen(false);
    shareTriggerRef.current?.focus();
  }

  function handleBookmark() {
    if (!requireAuth('bookmark')) return;
    void toggleBookmark().then((saved) =>
      toast(saved ? 'Saved to Bookmarks' : 'Removed from Bookmarks'),
    );
  }

  return (
    <ActionArea>
      <ActionDivider />
      <Row>
        {/* Comment — link to post detail page, or button when onComment is provided.
            Disabled on a deleted post (comment creation 409s server-side). */}
        {disabled ? (
          <DisabledHint explanation={explanation}>
            {(describedBy) => (
              <ActionBtn
                type="button"
                aria-label={`${post.comments} comments`}
                aria-disabled="true"
                aria-describedby={describedBy}
                onClick={suppressClick}
              >
                <Icon name="comment" size={iconSize} />
                {post.comments}
              </ActionBtn>
            )}
          </DisabledHint>
        ) : onComment ? (
          <ActionBtn type="button" aria-label={`${post.comments} comments`} onClick={onComment}>
            <Icon name="comment" size={iconSize} />
            {post.comments}
          </ActionBtn>
        ) : (
          <ActionLink to={postPath(post)} aria-label={`${post.comments} comments`}>
            <Icon name="comment" size={iconSize} />
            {post.comments}
          </ActionLink>
        )}

        {/* Cherish — local toggle, auth-gated. Disabled state keeps $active/aria-pressed
            reflecting the viewer's EXISTING reaction — deadening the handler must not
            visually erase a Cherish the viewer already gave before the post was deleted. */}
        {disabled ? (
          <DisabledHint explanation={explanation}>
            {(describedBy) => (
              <CherishBtn
                type="button"
                aria-label={`Cherish post, ${post.cheers} cherishes`}
                aria-disabled="true"
                aria-pressed={cheered}
                aria-describedby={describedBy}
                $active={cheered}
                onClick={suppressClick}
              >
                <Icon name="cherish" size={iconSize} />
                {post.cheers}
              </CherishBtn>
            )}
          </DisabledHint>
        ) : (
          <CherishBtn
            type="button"
            aria-label={`${cheered ? 'Take back your Cherish' : 'Cherish post'}, ${post.cheers} cherishes`}
            aria-pressed={cheered}
            $active={cheered}
            onClick={handleCherish}
          >
            <Icon name="cherish" size={iconSize} />
            {post.cheers}
          </CherishBtn>
        )}

        {/* Spread-It! — a toggle, auth-gated. Its own button, not a menu item: spreading
            is the one way a post reaches more people, so it sits beside Cherish with its
            count. Not offered at all for an unlisted video (spread 409s server-side). */}
        {canSpread &&
          (disabled ? (
            <DisabledHint explanation={explanation}>
              {(describedBy) => (
                <SpreadBtn
                  type="button"
                  aria-label={`Spread-It!, ${post.spreads} spreads`}
                  aria-disabled="true"
                  aria-pressed={hasSpread}
                  aria-describedby={describedBy}
                  $active={hasSpread}
                  onClick={suppressClick}
                >
                  <Icon name="spread-it" size={iconSize} />
                  {post.spreads}
                </SpreadBtn>
              )}
            </DisabledHint>
          ) : (
            <SpreadBtn
              type="button"
              aria-label={`${hasSpread ? 'Take back your spread' : 'Spread-It! to your followers'}, ${post.spreads} spreads`}
              aria-pressed={hasSpread}
              $active={hasSpread}
              onClick={handleSpread}
            >
              <Icon name="spread-it" size={iconSize} />
              {post.spreads}
            </SpreadBtn>
          ))}

        {/* Share — Popover with Quote / Share… items. Disabled on a deleted post: quoting is
            an interaction, so the whole affordance is gated — unlike "Copy link to post" in
            the ··· menu, which never mutates the post. */}
        {disabled ? (
          <DisabledHint explanation={explanation}>
            {(describedBy) => (
              <ActionBtn
                type="button"
                aria-label="Share"
                aria-disabled="true"
                aria-describedby={describedBy}
                onClick={suppressClick}
              >
                <Icon name="share" size={iconSize} />
              </ActionBtn>
            )}
          </DisabledHint>
        ) : (
          <Popover
            trigger={
              <ActionBtn ref={shareTriggerRef} type="button" aria-label="Share">
                <Icon name="share" size={iconSize} />
              </ActionBtn>
            }
            open={shareOpen}
            onOpenChange={setShareOpen}
          >
            <SpreadItem type="button" role="menuitem" onClick={handleQuote}>
              <Icon name="quote" size={iconSize} />
              Quote
            </SpreadItem>
            <SpreadItem type="button" role="menuitem" onClick={handleShareSheet}>
              <Icon name="share" size={iconSize} />
              Share…
            </SpreadItem>
          </Popover>
        )}

        {/* Share sheet (1.23.90) — portals into <body>; mounted only while open so the
            message draft resets between opens. */}
        {shareDialogOpen && <SharePostDialog post={post} onClose={closeShareSheet} />}

        {/* Bookmark — only rendered when showBookmark is true (post-detail hero).
            Stays live even on a deleted post — bookmarking is always allowed. */}
        {showBookmark && (
          <BookmarkBtn
            type="button"
            aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark post'}
            aria-pressed={isBookmarked}
            $active={isBookmarked}
            onClick={handleBookmark}
          >
            <Icon name="bookmark" size={iconSize} />
          </BookmarkBtn>
        )}
      </Row>
    </ActionArea>
  );
}
